/**
 * Inline "choose a printer" list for the Printer Helper's troubleshoot view —
 * the first step when no printer has been saved yet. Scan → list → select →
 * connect → save, reusing the helper's Card/List styling (no separate dialog).
 *
 * Bluetooth is checked before scanning a Bluetooth interface; when it's off the
 * list shows a clear enable prompt instead of silently rescanning.
 */
import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import List from '@mui/material/List';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import ListItemText from '@mui/material/ListItemText';
import MuiSearchIcon from '@mui/icons-material/Search';
import ListItemButton from '@mui/material/ListItemButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import MuiErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { promptEnableBluetooth } from 'src/hooks/use-device-permissions';

import { usePrinterStore } from 'src/store/PrinterStore';
import { getPlatformPrinter } from 'src/platform/services';
import { checkPrinterReadiness } from 'src/services/printing';
import { toSavedPrinter, type StarPrinter } from 'src/services/printers';

import MuiIcon from 'src/components/mui-icon';

import { usesDesktopLanSetup } from 'src/sections/settings/tabs/system/printers/lan/desktop-lan';
import { usePrinterDiscovery } from 'src/sections/settings/tabs/system/printers/use-printer-discovery';
import DesktopAddPrinterEntry from 'src/sections/settings/tabs/system/printers/lan/desktop-add-printer-entry';
import {
  issueLabel,
  interfaceIcon,
} from 'src/sections/settings/tabs/system/printers/printer-interface-meta';

// ----------------------------------------------------------------------

interface AvailablePrintersListProps {
  /** Called after a printer is connected, saved and set active. */
  onConnected?: () => void;
}

/**
 * A desktop register (Star LAN) adds printers through the one desktop flow —
 * discovery or IP address → check → test page → save — not this SDK scan list.
 * A module constant, so the same branch renders for the component's whole life.
 */
const desktopLan = usesDesktopLanSetup(getPlatformPrinter().thermalPrinting);

export default function AvailablePrintersList({ onConnected }: AvailablePrintersListProps) {
  return desktopLan ? (
    <DesktopAddPrinterEntry onConnected={onConnected} />
  ) : (
    <SdkAvailablePrintersList onConnected={onConnected} />
  );
}

function SdkAvailablePrintersList({ onConnected }: AvailablePrintersListProps) {
  const {
    printers,
    issues,
    loading,
    includeBle,
    includeUsb,
    setIncludeBle,
    setIncludeUsb,
    connectingId,
    bluetoothOff,
    discover,
    connect,
  } = usePrinterDiscovery({ guardBluetooth: true });

  const activePrinterId = usePrinterStore((s) => s.activePrinter?.identifier ?? null);
  const isAndroid = Capacitor.getPlatform() === 'android';

  // Scan once when this step first appears; further scans are user-driven.
  useEffect(() => {
    discover();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = async (printer: StarPrinter) => {
    if (!(await connect(printer))) return;
    await usePrinterStore.getState().setActivePrinter(toSavedPrinter(printer));
    await checkPrinterReadiness({ force: true });
    onConnected?.();
  };

  const handleEnableBluetooth = async () => {
    await promptEnableBluetooth();
    discover();
  };

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexWrap: 'wrap', gap: 1 }}
      >
        <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeBle}
                onChange={(e) => setIncludeBle(e.target.checked)}
              />
            }
            label="Include BLE"
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeUsb}
                onChange={(e) => setIncludeUsb(e.target.checked)}
              />
            }
            label="Include USB"
          />
        </Stack>
        <LoadingButton
          size="small"
          variant="outlined"
          loading={loading}
          onClick={discover}
          startIcon={<MuiIcon icon={MuiSearchIcon} />}
        >
          Scan
        </LoadingButton>
      </Stack>

      {/* Bluetooth is off but a Bluetooth interface was requested. */}
      {bluetoothOff && (
        <Alert
          severity="warning"
          action={
            isAndroid ? (
              <Button color="warning" size="small" onClick={handleEnableBluetooth}>
                Enable
              </Button>
            ) : undefined
          }
        >
          Bluetooth is off.{' '}
          {isAndroid
            ? 'Turn it on to find Bluetooth printers.'
            : 'Turn it on in Control Center, then Scan again.'}
        </Alert>
      )}

      {/* Per-interface issues (permissions, network, skipped interface). */}
      {!loading &&
        issues.map((issue, i) => (
          <Alert key={`${issue.code}-${i}`} severity="warning" sx={{ py: 0.25 }}>
            <strong>{issueLabel(issue.interfaceType)}:</strong> {issue.message}
          </Alert>
        ))}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && printers.length > 0 && (
        <Card sx={{ p: 0 }}>
          <List sx={{ p: 0 }}>
            {printers.map((printer) => (
              <ListItem key={printer.identifier} sx={{ p: 0 }}>
                <ListItemButton
                  onClick={() => handleSelect(printer)}
                  disabled={connectingId !== null}
                  selected={printer.identifier === activePrinterId}
                  sx={{ p: 2, '&.Mui-selected': { bgcolor: 'primary.lighter' } }}
                >
                  <MuiIcon
                    icon={interfaceIcon(printer.interfaceType)}
                    width={24}
                    sx={{ mr: 2, color: 'text.secondary', flexShrink: 0 }}
                  />
                  <ListItemText
                    primary={printer.name ?? printer.modelName ?? printer.model}
                    secondary={`${printer.model} · ${printer.interfaceType.toUpperCase()}`}
                  />
                  {connectingId === printer.identifier && <CircularProgress size={20} />}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Card>
      )}

      {!loading && !bluetoothOff && printers.length === 0 && issues.length === 0 && (
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <MuiIcon icon={MuiErrorOutlineIcon} width={40} sx={{ color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            No printers found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Make sure the printer is on and on the same network, then Scan again. For Bluetooth-LE
            models, turn on “Include BLE”.
          </Typography>
        </Card>
      )}
    </Stack>
  );
}
